import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as LoadingIcons from "react-loading-icons";
import { getDevis } from "../services/DevisService";
import { useSelector } from "react-redux";
import { FaPrint, FaEnvelope, FaPhone, FaMapMarkerAlt, FaUser } from "react-icons/fa";
import { tvainfo } from "../services/LoginService";

const ConsulterDevisComptable = () => {
    const { id } = useParams();
    const [devis, setDevis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPdf, setSelectedPdf] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(true);
    const token = useSelector((state) => state.authComptable.token);
    const [tva_inclus, setTvaInclus] = useState(false);
    const [activeRates, setActiveRates] = useState([]);

    useEffect(() => {
        const fetchDevis = async () => {
            try {
                // ✅ Passer le token au service
                const devisInstance = await getDevis(id, token);
                setDevis(devisInstance);

                const resTVA = await tvainfo(token);
                if (resTVA.success) {
                    setTvaInclus(resTVA.tva_inclus ?? false);
                    const rates = resTVA.tva_rates || {};
                    const active = Object.keys(rates)
                        .filter(k => rates[k])
                        .sort((a, b) => parseFloat(a) - parseFloat(b));
                    setActiveRates(active);
                }
            } catch (err) {
                toast.error(err.message);
                // ✅ Redirection correcte pour comptable
                window.location.href = "/EspaceCompta/devis";
            } finally {
                setLoading(false);
            }
        };

        fetchDevis();
    }, [id, token]);

    const tvaParTaux = useMemo(() => {
        const result = {};
        activeRates.forEach(rate => result[parseFloat(rate).toFixed(2)] = 0);
        devis?.lignes?.forEach(ligne => {
            const totalHT = parseFloat(ligne.total_ligne_centimes) || 0;
            const tauxTVA = (parseFloat(ligne.taux_tva) || 0).toFixed(2);
            const montantTVA = (totalHT * parseFloat(tauxTVA)) / 100;
            if (!result[tauxTVA]) result[tauxTVA] = 0;
            result[tauxTVA] += montantTVA;
        });
        return result;
    }, [devis, activeRates]);

    const totaux = useMemo(() => {
        let sous_total = 0, remise = 0, tva = 0;
        devis?.lignes?.forEach(ligne => {
            const prix = parseFloat(ligne.prix_unitaire_centimes) || 0;
            const quantite = parseFloat(ligne.quantite) || 0;
            const tauxRemise = parseFloat(ligne.taux_remise) || 0;
            const tauxTVA = parseFloat(ligne.taux_tva) || 0;
            const ligneTotal = prix * quantite;
            const ligneRemise = ligneTotal * tauxRemise / 100;
            const ligneHT = ligneTotal - ligneRemise;
            const ligneTVA = tva_inclus ? ligneHT * tauxTVA / 100 : 0;
            sous_total += ligneHT;
            remise += ligneRemise;
            tva += ligneTVA;
        });
        return {
            sous_total_centimes: sous_total,
            remise_centimes: remise,
            tva_centimes: tva,
            total_centimes: sous_total + tva
        };
    }, [devis, tva_inclus]);

    if (loading)
        return (
            <div className="flex justify-center items-center mt-20">
                <LoadingIcons.SpinningCircles stroke="#2218eeff" fill="#2218eeff" strokeWidth={3} height={40} width={40} />
                <span className="ml-3 text-gray-600 font-medium">Chargement du devis...</span>
            </div>
        );

    if (!devis) return null;

    const formatMoney = (value) => (value || 0).toFixed(2) + " €";

    const openPdf = (id) => {
        const pdfUrl = `http://localhost:8000/api/v1/devis/${id}/pdf?token=${token}`;
        setSelectedPdf(pdfUrl);
        setPdfLoading(true);
    };

    return (
        <div className="min-h-screen p-6 flex flex-col gap-6">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* PDF Modal */}
            {selectedPdf && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-transparent w-11/12 h-full rounded-lg shadow-lg p-2 relative">
                        <button
                            onClick={() => setSelectedPdf(null)}
                            className="absolute top-2 right-2 text-white bg-red-600 px-2 py-1 rounded">
                            X
                        </button>

                        {pdfLoading && (
                            <div className="flex justify-center items-center h-full">
                                <LoadingIcons.SpinningCircles stroke="#fff" fill="#fff" strokeWidth={3} height={40} width={40} />
                                <span className="ml-3 text-white font-medium">Chargement du PDF...</span>
                            </div>
                        )}

                        <iframe
                            src={selectedPdf}
                            title="Devis PDF"
                            className={`w-full h-full rounded-lg ${pdfLoading ? "hidden" : ""}`}
                            onLoad={() => setPdfLoading(false)}
                        />
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h1 className="font-bold text-2xl">Devis #{devis.numero}</h1>
                <div className="flex gap-2">
                    <button
                        onClick={() => openPdf(devis.id)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2">
                        <FaPrint /> Pdf
                    </button>
                </div>
            </div>

            {/* Secteurs */}
            {(devis.secteurs_batiment.length > 0 || devis.secteurs_autres.length > 0) && (
                <div className="flex flex-wrap gap-2 mb-4">
                    {devis.secteurs_batiment.map((secteur) => (
                        <span key={`bat-${secteur.id}`} className="px-3 py-1 rounded-full bg-green-500 text-white text-sm font-medium">
                            {secteur.nom}
                        </span>
                    ))}
                    {devis.secteurs_autres.map((secteur) => (
                        <span key={`autre-${secteur.id}`} className="px-3 py-1 rounded-full bg-gray-400 text-white text-sm font-medium">
                            {secteur.nom}
                        </span>
                    ))}
                </div>
            )}

            {/* Émetteur & Client */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                    <h2 className="font-semibold mb-2 flex items-center gap-2"><FaUser /> Émetteur</h2>
                    {devis.user?.nom_complet && <p><FaUser className="inline mr-1" /> {devis.user.nom_complet}</p>}
                    {devis.user?.email && <p><FaEnvelope className="inline mr-1" /> {devis.user.email}</p>}
                    {devis.user?.telephone && <p><FaPhone className="inline mr-1" /> {devis.user.telephone}</p>}
                    {devis.user?.adresse_ligne1 && <p><FaMapMarkerAlt className="inline mr-1" /> {devis.user.adresse_ligne1}</p>}
                    {devis.user?.adresse_ligne2 && <p><FaMapMarkerAlt className="inline mr-1" /> {devis.user.adresse_ligne2}</p>}
                    {devis.user?.ville && <p><FaMapMarkerAlt className="inline mr-1" /> {devis.user.ville} {devis.user.code_postal}</p>}
                </div>

                <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                    <h2 className="font-semibold mb-2 flex items-center gap-2"><FaUser /> Destinataire</h2>
                    {devis.client?.nom_affichage && <p><FaUser className="inline mr-1" /> {devis.client.nom_affichage}</p>}
                    {devis.client?.nom_contact && <p><FaUser className="inline mr-1" /> Contact: {devis.client.nom_contact}</p>}
                    {devis.client?.email && <p><FaEnvelope className="inline mr-1" /> {devis.client.email}</p>}
                    {devis.client?.telephone && <p><FaPhone className="inline mr-1" /> {devis.client.telephone}</p>}
                    {devis.client?.adresse_ligne1 && <p><FaMapMarkerAlt className="inline mr-1" /> {devis.client.adresse_ligne1}</p>}
                    {devis.client?.adresse_ligne2 && <p><FaMapMarkerAlt className="inline mr-1" /> {devis.client.adresse_ligne2}</p>}
                    {devis.client?.ville && <p><FaMapMarkerAlt className="inline mr-1" /> {devis.client.code_postal} {devis.client.ville}</p>}
                    {devis.client?.numero_tva && <p>TVA : {devis.client.numero_tva}</p>}
                    {devis.client?.siret && <p>SIRET : {devis.client.siret}</p>}
                </div>
            </div>

            {/* Infos devis */}
            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-2">
                <h2 className="font-semibold mb-2">Informations du devis</h2>
                <p><strong>Date d'émission :</strong> {devis.date_emission}</p>
                <p><strong>Date d'échéance :</strong> {devis.date_expiration}</p>
                <p><strong>Statut :</strong> {devis.statut}</p>
                {devis.titre && <p><strong>Titre :</strong> {devis.titre}</p>}
                {devis.notes_client && <p><strong>Notes client :</strong> {devis.notes_client}</p>}
                {devis.notes_internes && <p><strong>Notes internes :</strong> {devis.notes_internes}</p>}
            </div>

            {/* Lignes */}
            <div className="bg-white shadow-md rounded-lg p-4">
                <h2 className="font-semibold mb-2">Lignes</h2>
                <table className="w-full text-sm border-collapse text-center">
                    <thead className="bg-color1 text-color2 uppercase text-xs font-semibold">
                        <tr>
                            <th className="px-3 py-2">Désignation</th>
                            <th className="px-3 py-2 w-24">Qté</th>
                            <th className="px-3 py-2 w-24">Unité</th>
                            <th className="px-3 py-2 w-32">Prix Unitaire</th>
                            {devis.remise_centimes > 0 && <th className="px-3 py-2 w-24">Remise (%)</th>}
                            {devis.tva_centimes > 0 && <th className="px-3 py-2 w-24">TVA (%)</th>}
                            <th className="px-3 py-2 w-32">Total HT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {devis.lignes.map((ligne, index) => (
                            <tr key={index} className="border-b hover:bg-gray-50">
                                <td className="px-2 py-1 text-left">{ligne.designation}</td>
                                <td className="px-2 py-1">{ligne.quantite}</td>
                                <td className="px-2 py-1">{ligne.unite || "-"}</td>
                                <td className="px-2 py-1">{formatMoney(ligne.prix_unitaire_centimes)}</td>
                                {devis.remise_centimes > 0 && <td className="px-2 py-1">{ligne.taux_remise || "-"}</td>}
                                {devis.tva_centimes > 0 && <td className="px-2 py-1">{ligne.taux_tva || "-"}</td>}
                                <td className="px-2 py-1">{formatMoney((ligne.prix_unitaire_centimes || 0) * (ligne.quantite || 0) - ((ligne.prix_unitaire_centimes || 0) * (ligne.quantite || 0) * (ligne.taux_remise || 0) / 100))}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Totaux */}
                <div className="grid grid-cols-[110px_100px] gap-y-1 justify-end items-end mt-2 border-t pt-2">
                    {totaux.sous_total_centimes > 0 && (
                        <>
                            <span className="font-bold text-left">Total HT :</span>
                            <span className="text-right">{totaux.sous_total_centimes.toFixed(2)} €</span>
                        </>
                    )}
                    {totaux.remise_centimes > 0 && (
                        <>
                            <span className="font-bold text-left">Remise :</span>
                            <span className="text-right">{totaux.remise_centimes.toFixed(2)} €</span>
                        </>
                    )}
                    {tva_inclus &&
                        activeRates
                            .filter((rate) => (tvaParTaux[parseFloat(rate).toFixed(2)] || 0) > 0)
                            .map((rate) => {
                                const key = parseFloat(rate).toFixed(2);
                                return (
                                    <React.Fragment key={key}>
                                        <span className="font-bold text-left">TVA ({rate}) :</span>
                                        <span className="text-right">{tvaParTaux[key].toFixed(2)} €</span>
                                    </React.Fragment>
                                );
                            })}
                    {totaux.tva_centimes > 0 && (
                        <>
                            <span className="font-bold text-left">Total TVA :</span>
                            <span className="text-right">{totaux.tva_centimes.toFixed(2)} €</span>
                            <span className="font-bold text-left">Total TTC :</span>
                            <span className="text-right">{totaux.total_centimes.toFixed(2)} €</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConsulterDevisComptable;
